// Cliente de autenticación compatible con `supabase.auth`.
//
// Guarda la sesión en localStorage, la refresca sola antes de que caduque y
// sincroniza entre pestañas, igual que hacía supabase-js. Los nombres y las
// formas de retorno son los mismos, así que useAuth.ts y las páginas de
// callback/reset funcionan sin tocarlas.

import { GatewayError, request, setTokenProvider } from './transport';
import type {
  AuthChangeEvent,
  AuthError,
  AuthResponse,
  Session,
  SessionResponse,
  Subscription,
  User,
  UserResponse,
} from './types';

const STORAGE_KEY = 'contabird.auth.session';

/**
 * Dónde se guarda la sesión del administrador mientras inspecciona a otro.
 *
 * Su sesión NO se cierra: se aparta. Volver de una inspección tiene que ser instantáneo y no
 * puede depender de que el admin recuerde su contraseña ni de que el servidor siga vivo.
 */
const INSPECTOR_KEY = 'contabird.auth.inspector';

/** Datos de la inspección en curso, para pintar el aviso. */
export interface InspectionInfo {
  inspectorEmail: string;
  targetEmail: string;
  targetId: string;
  copiedTables?: number;
  copiedRows?: number;
}

// Margen para renovar antes de que el token caduque de verdad.
const REFRESH_MARGIN_SECONDS = 60;

const toAuthError = (err: unknown): AuthError => {
  if (err instanceof GatewayError) {
    return { name: 'AuthApiError', message: err.message, status: err.status, code: err.code };
  }
  return {
    name: 'AuthError',
    message: err instanceof Error ? err.message : String(err),
  };
};

const isExpired = (session: Session | null, marginSeconds = 0): boolean => {
  if (!session?.expires_at) return false;
  return session.expires_at * 1000 - marginSeconds * 1000 <= Date.now();
};

type Listener = (event: AuthChangeEvent, session: Session | null) => void;

export class AuthClient {
  private session: Session | null = null;
  private listeners = new Map<string, Listener>();
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshing: Promise<Session | null> | null = null;
  private initialized = false;

  constructor() {
    this.session = this.readStorage();

    // El transporte pide el token en cada llamada al gateway; así siempre viaja
    // uno vigente sin que el resto del código se entere del refresco.
    setTokenProvider(async () => {
      const session = await this.ensureFreshSession();
      return session?.access_token ?? null;
    });

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (event) => {
        if (event.key !== STORAGE_KEY) return;
        // Otra pestaña inició o cerró sesión.
        const next = this.readStorage();
        const changed = next?.access_token !== this.session?.access_token;
        this.session = next;
        if (changed) {
          this.scheduleRefresh();
          this.emit(next ? 'SIGNED_IN' : 'SIGNED_OUT', next);
        }
      });

      this.scheduleRefresh();

      // Se avisa del estado inicial en el siguiente tick para que quien llame a
      // onAuthStateChange justo después de montar reciba el evento.
      setTimeout(() => {
        this.initialized = true;
        this.emit('INITIAL_SESSION', this.session);
      }, 0);
    }
  }

  // ─── Persistencia ──────────────────────────────────────────────────────────

  private readStorage(): Session | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Session;
      return parsed?.access_token && parsed?.refresh_token ? parsed : null;
    } catch {
      return null;
    }
  }

  private writeStorage(session: Session | null) {
    if (typeof window === 'undefined') return;
    try {
      if (session) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Modo privado o almacenamiento lleno: la sesión sigue viva en memoria.
    }
  }

  private setSessionInternal(session: Session | null, event: AuthChangeEvent) {
    this.session = session;
    this.writeStorage(session);
    this.scheduleRefresh();
    this.emit(event, session);
  }

  // ─── Eventos ───────────────────────────────────────────────────────────────

  private emit(event: AuthChangeEvent, session: Session | null) {
    for (const listener of this.listeners.values()) {
      try {
        listener(event, session);
      } catch (err) {
        console.error('[auth] error en un listener de onAuthStateChange:', err);
      }
    }
  }

  onAuthStateChange(callback: Listener): { data: { subscription: Subscription } } {
    const id = Math.random().toString(36).slice(2);
    this.listeners.set(id, callback);

    // Si la sesión inicial ya se emitió, este suscriptor la recibe igualmente.
    if (this.initialized) {
      setTimeout(() => {
        if (this.listeners.has(id)) callback('INITIAL_SESSION', this.session);
      }, 0);
    }

    return {
      data: {
        subscription: {
          id,
          unsubscribe: () => {
            this.listeners.delete(id);
          },
        },
      },
    };
  }

  // ─── Refresco automático ───────────────────────────────────────────────────

  private scheduleRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (typeof window === 'undefined' || !this.session?.expires_at) return;

    const msUntilRefresh = this.session.expires_at * 1000 - Date.now() - REFRESH_MARGIN_SECONDS * 1000;
    this.refreshTimer = setTimeout(() => {
      void this.refreshSession();
    }, Math.max(msUntilRefresh, 5_000));
  }

  /** Devuelve la sesión renovándola antes si está a punto de caducar. */
  private async ensureFreshSession(): Promise<Session | null> {
    if (!this.session) return null;
    if (!isExpired(this.session, REFRESH_MARGIN_SECONDS)) return this.session;
    return this.refreshSession();
  }

  private async refreshSession(): Promise<Session | null> {
    if (this.refreshing) return this.refreshing;

    const refreshToken = this.session?.refresh_token;
    if (!refreshToken) return null;

    this.refreshing = (async () => {
      try {
        const payload = await request<{ session: Session }>('/auth/token?grant_type=refresh_token', {
          method: 'POST',
          body: { refresh_token: refreshToken },
          auth: false,
        });
        this.setSessionInternal(payload.session, 'TOKEN_REFRESHED');
        return payload.session;
      } catch (err) {
        // Un refresh token revocado o caducado significa sesión terminada.
        if (err instanceof GatewayError && err.status === 401) {
          this.setSessionInternal(null, 'SIGNED_OUT');
          return null;
        }
        console.warn('[auth] no se pudo refrescar la sesión:', err);
        return this.session;
      } finally {
        this.refreshing = null;
      }
    })();

    return this.refreshing;
  }

  // ─── API pública ───────────────────────────────────────────────────────────

  async getSession(): Promise<SessionResponse> {
    try {
      const session = await this.ensureFreshSession();
      return { data: { session }, error: null };
    } catch (err) {
      return { data: { session: null }, error: toAuthError(err) };
    }
  }

  /**
   * Devuelve el usuario de la sesión vigente. supabase-js hacía una llamada de
   * red en cada invocación; aquí se resuelve en local (el JWT está firmado y el
   * gateway lo valida en cada consulta igualmente), lo que evita cientos de
   * peticiones porque la aplicación llama a getUser() en casi todas las pantallas.
   */
  async getUser(jwt?: string): Promise<UserResponse> {
    try {
      if (jwt) {
        const payload = await request<{ user: User }>('/auth/user', {
          method: 'GET',
          auth: false,
          headers: { authorization: `Bearer ${jwt}` },
        });
        return { data: { user: payload.user }, error: null };
      }

      const session = await this.ensureFreshSession();
      return { data: { user: session?.user ?? null }, error: session ? null : null };
    } catch (err) {
      return { data: { user: null }, error: toAuthError(err) };
    }
  }

  /** Vuelve a leer el usuario del servidor y actualiza la sesión guardada. */
  async refreshUser(): Promise<UserResponse> {
    try {
      const session = await this.ensureFreshSession();
      if (!session) return { data: { user: null }, error: null };

      const payload = await request<{ user: User }>('/auth/user', { method: 'GET' });
      this.setSessionInternal({ ...session, user: payload.user }, 'USER_UPDATED');
      return { data: { user: payload.user }, error: null };
    } catch (err) {
      return { data: { user: null }, error: toAuthError(err) };
    }
  }

  async signUp(credentials: {
    email: string;
    password: string;
    options?: { data?: Record<string, unknown>; emailRedirectTo?: string };
  }): Promise<AuthResponse> {
    try {
      const payload = await request<{ user: User | null; session: Session | null }>('/auth/signup', {
        method: 'POST',
        auth: false,
        body: {
          email: credentials.email,
          password: credentials.password,
          data: credentials.options?.data ?? {},
        },
      });

      if (payload.session) this.setSessionInternal(payload.session, 'SIGNED_IN');

      return { data: { user: payload.user, session: payload.session }, error: null };
    } catch (err) {
      return { data: { user: null, session: null }, error: toAuthError(err) };
    }
  }

  async signInWithPassword(credentials: { email: string; password: string }): Promise<AuthResponse> {
    try {
      const payload = await request<{ session: Session; user: User }>(
        '/auth/token?grant_type=password',
        {
          method: 'POST',
          auth: false,
          body: { email: credentials.email, password: credentials.password },
        },
      );

      this.setSessionInternal(payload.session, 'SIGNED_IN');
      return { data: { user: payload.session.user, session: payload.session }, error: null };
    } catch (err) {
      return { data: { user: null, session: null }, error: toAuthError(err) };
    }
  }

  async signOut(options?: { scope?: 'global' | 'local' }): Promise<{ error: AuthError | null }> {
    const refreshToken = this.session?.refresh_token;

    // Cerrar sesión estando dentro de una inspección debe llevarse también la sesión apartada del
    // admin; si no, al volver a entrar el aviso reaparecería sobre una sesión que ya no existe.
    if (typeof window !== 'undefined') window.localStorage.removeItem(INSPECTOR_KEY);

    // La sesión local se limpia siempre, aunque el servidor no responda.
    this.setSessionInternal(null, 'SIGNED_OUT');

    try {
      if (refreshToken) {
        await request(`/auth/logout?scope=${options?.scope ?? 'local'}`, {
          method: 'POST',
          auth: false,
          body: { refresh_token: refreshToken },
        });
      }
      return { error: null };
    } catch {
      // Cerrar sesión nunca debe fallar de cara al usuario.
      return { error: null };
    }
  }

  async updateUser(attributes: {
    email?: string;
    password?: string;
    data?: Record<string, unknown>;
  }): Promise<UserResponse> {
    try {
      const payload = await request<{ user: User }>('/auth/user', {
        method: 'PUT',
        body: attributes,
      });

      if (this.session) {
        this.setSessionInternal({ ...this.session, user: payload.user }, 'USER_UPDATED');
      }

      return { data: { user: payload.user }, error: null };
    } catch (err) {
      return { data: { user: null }, error: toAuthError(err) };
    }
  }

  async resetPasswordForEmail(
    email: string,
    options?: { redirectTo?: string },
  ): Promise<{ data: Record<string, never>; error: AuthError | null }> {
    try {
      await request('/auth/recover', {
        method: 'POST',
        auth: false,
        body: { email, redirectTo: options?.redirectTo },
      });
      return { data: {}, error: null };
    } catch (err) {
      return { data: {}, error: toAuthError(err) };
    }
  }

  /** Instala una sesión ya emitida (enlaces con access_token + refresh_token). */
  async setSession(tokens: { access_token: string; refresh_token: string }): Promise<AuthResponse> {
    try {
      const payload = await request<{ session: Session }>('/auth/token?grant_type=refresh_token', {
        method: 'POST',
        auth: false,
        body: { refresh_token: tokens.refresh_token },
      });
      this.setSessionInternal(payload.session, 'SIGNED_IN');
      return { data: { user: payload.session.user, session: payload.session }, error: null };
    } catch (err) {
      return { data: { user: null, session: null }, error: toAuthError(err) };
    }
  }

  // ─── Inspección de cuentas ─────────────────────────────────────────────────

  /** ¿Hay una inspección en curso? Lo delata la sesión del admin apartada. */
  isInspecting(): boolean {
    if (typeof window === 'undefined') return false;
    return !!window.localStorage.getItem(INSPECTOR_KEY);
  }

  /** Datos de la inspección en curso, o null. */
  inspectionInfo(): InspectionInfo | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(INSPECTOR_KEY);
      if (!raw) return null;
      return (JSON.parse(raw) as { info: InspectionInfo }).info ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Entra a inspeccionar la cuenta indicada. Quién puede hacerlo lo decide el servidor.
   *
   * La sesión propia se aparta ANTES de instalar la nueva: si algo falla a mitad, el admin no se
   * queda sin ninguna de las dos.
   */
  async startInspection(targetUserId: string): Promise<{ error: AuthError | null; info?: InspectionInfo }> {
    try {
      const current = this.session;
      if (!current) throw new Error('No hay sesión activa');

      const payload = await request<{ session: Session; inspection: InspectionInfo }>('/auth/inspect', {
        method: 'POST',
        body: { target_user_id: targetUserId },
      });

      window.localStorage.setItem(
        INSPECTOR_KEY,
        JSON.stringify({ session: current, info: payload.inspection }),
      );

      this.setSessionInternal(payload.session, 'SIGNED_IN');
      return { error: null, info: payload.inspection };
    } catch (err) {
      return { error: toAuthError(err) };
    }
  }

  /**
   * Sale de la inspección y devuelve al admin su propia sesión.
   *
   * La sesión guardada se restaura pase lo que pase con la llamada al servidor: si el borrado del
   * sandbox falla, el barrido de huérfanos lo recogerá, pero dejar al admin atrapado dentro de la
   * cuenta de un cliente no es una opción.
   */
  async stopInspection(): Promise<{ error: AuthError | null }> {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(INSPECTOR_KEY) : null;
    const stashed = raw ? (JSON.parse(raw) as { session: Session }) : null;

    try {
      await request('/auth/inspect/stop', {
        method: 'POST',
        body: { refresh_token: this.session?.refresh_token ?? null },
      });
    } catch (err) {
      console.warn('[auth] no se pudo cerrar la inspección en el servidor:', err);
    }

    if (typeof window !== 'undefined') window.localStorage.removeItem(INSPECTOR_KEY);

    if (stashed?.session) {
      this.setSessionInternal(stashed.session, 'SIGNED_IN');
      return { error: null };
    }

    this.setSessionInternal(null, 'SIGNED_OUT');
    return { error: null };
  }

  /** Canjea el código de un enlace de recuperación por una sesión. */
  async exchangeCodeForSession(code: string): Promise<AuthResponse> {
    try {
      const payload = await request<{ session: Session }>('/auth/verify', {
        method: 'POST',
        auth: false,
        body: { token: code },
      });
      this.setSessionInternal(payload.session, 'PASSWORD_RECOVERY');
      return { data: { user: payload.session.user, session: payload.session }, error: null };
    } catch (err) {
      return { data: { user: null, session: null }, error: toAuthError(err) };
    }
  }

  /** Token en crudo, para las llamadas a /api que lo esperan en el body. */
  getAccessToken(): string | null {
    return this.session?.access_token ?? null;
  }
}
